import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Image as ImageIcon, Plus, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import LanguageSelect from "./release-editor/LanguageSelect";
import DatePickerField from "./release-editor/DatePickerField";
import ContribList from "./release-editor/ContribList";
import TrackRow from "./release-editor/TrackRow";
import { ServiceRestrictions, TerritoryRestrictions } from "./release-editor/Restrictions";
import ArtistsEditor from "./release-editor/ArtistsEditor";
import {
  inputCls, labelCls, emptyAlbum, emptyTrack, SERVICES, VIDEO_SERVICES,
} from "./release-editor/constants";

export default function ReleaseEditorDialog({ open, onOpenChange, onSaved, initial, initialType }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("details");
  const [album, setAlbum] = useState({ ...emptyAlbum, ...(initial || {}), ...(initialType ? { type: initialType } : {}) });
  const [tracks, setTracks] = useState(initial?.tracks?.length ? initial.tracks : [emptyTrack()]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ cover: false, booklet: false });

  // Sync album.type with the parent-provided initialType each time the dialog opens.
  // This makes the "New music video" button reliably pre-select the Music Video type.
  useEffect(() => {
    if (open && initialType) {
      setAlbum((a) => ({ ...a, type: initialType }));
      setTab("details");
    }
  }, [open, initialType]);

  // Apply role-based defaults (label/artist) when opening a fresh draft.
  useEffect(() => {
    if (!open) return;
    if (initial?.release_id) return; // editing an existing release — don't override
    setAlbum((a) => ({
      ...a,
      label: a.label || (user?.role === "label" ? (user?.display_name || user?.name || "") : "Noizbloc Private Limited"),
      parent_label: "Noizbloc Private Limited",
      artists: (a.artists && a.artists.length > 0)
        ? a.artists
        : [{ name: user?.display_name || user?.name || "", role: "primary", spotify_url: user?.spotify_artist_url || "", spotify_id: user?.spotify_artist_id || "", spotify_image: user?.spotify_image_url || "", apple_music_url: user?.apple_music_artist_url || "" }],
    }));
  }, [open, user, initial?.release_id]);

  const setA = (k, v) => setAlbum((a) => ({ ...a, [k]: v }));

  const isVideo = album.type === "Music Video";

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading((u) => ({ ...u, cover: true }));
    try {
      // Music-video releases upload a landscape thumbnail (JPG/PNG, min 1920×1080).
      // Everything else uploads square cover art (JPG/PNG/WEBP, ~3000×3000).
      const kind = isVideo ? "thumbnail" : "image";
      const res = await uploadFile(file, kind);
      setA("cover_url", res.url);
      toast.success(isVideo ? "Thumbnail uploaded" : "Cover uploaded");
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setUploading((u) => ({ ...u, cover: false }));
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!album.title.trim()) return toast.error("Give your release a title");
    if (!tracks.length) return toast.error("Add at least one track");
    for (const [i, t] of tracks.entries()) {
      if (!t.title.trim()) return toast.error(`Track ${i + 1} needs a title`);
    }
    setSaving(true);
    try {
      const payload = {
        ...album,
        tracks: tracks.map((t, i) => ({
          ...t,
          track_number: t.track_number || i + 1,
          writers: t.writers.filter((w) => w.name.trim()),
          publishers: t.publishers.filter((p) => p.name.trim()),
        })),
      };
      const { data } = await api.post("/artist/releases", payload);
      toast.success("Release draft created");
      onSaved && onSaved(data);
      onOpenChange(false);
      setAlbum(emptyAlbum);
      setTracks([emptyTrack()]);
      setTab("details");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to create release"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="release-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            {isVideo ? "Create a music video" : "Create a release"}
            {isVideo && (
              <span
                data-testid="mv-badge"
                className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-300"
              >
                Music Video
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {isVideo
              ? "Deliver your music video to 18 video-first stores (VEVO/YouTube, Apple Music Video, Spotify Video, XITE and more). Fill in the details and attach your video file on the Tracks tab."
              : "Fill in the album, tracks, rights and distribution details. Saved as a draft — submit for review when ready."}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="bg-white/5 border border-white/10 h-auto p-1 flex flex-wrap gap-1">
            <TabsTrigger value="details" data-testid="tab-details" className="data-[state=active]:bg-white data-[state=active]:text-black text-slate-300">Album details</TabsTrigger>
            <TabsTrigger value="tracks" data-testid="tab-tracks" className="data-[state=active]:bg-white data-[state=active]:text-black text-slate-300">Tracks ({tracks.length})</TabsTrigger>
            <TabsTrigger value="rights" data-testid="tab-rights" className="data-[state=active]:bg-white data-[state=active]:text-black text-slate-300">Rights &amp; Credits</TabsTrigger>
            <TabsTrigger value="distribution" data-testid="tab-distribution" className="data-[state=active]:bg-white data-[state=active]:text-black text-slate-300">Distribution</TabsTrigger>
          </TabsList>

          <form onSubmit={submit} className="flex-1 min-h-0 overflow-y-auto pt-4" data-testid="release-editor-form">
            <TabsContent value="details" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Title *</label>
                  <input data-testid="album-title" className={inputCls} value={album.title} onChange={(e) => setA("title", e.target.value)} placeholder="Release title" />
                </div>
                <div>
                  <label className={labelCls}>Type</label>
                  <Select value={album.type} onValueChange={(v) => setA("type", v)}>
                    <SelectTrigger data-testid="album-type" className="bg-white/5 border-white/10 text-white h-[42px] rounded-xl text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="EP">EP</SelectItem>
                      <SelectItem value="Album">Album</SelectItem>
                      <SelectItem value="Music Video">Music Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className={labelCls}>Version</label>
                  <input className={inputCls} value={album.version} onChange={(e) => setA("version", e.target.value)} placeholder="e.g. Deluxe, Remastered" />
                </div>
                <div className="md:col-span-2">
                  <ArtistsEditor
                    artists={album.artists || []}
                    onChange={(next) => setA("artists", next)}
                    fallbackName={album.display_artist}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Display Artist <span className="text-slate-500 normal-case tracking-normal">(auto — override if needed)</span></label>
                  <input className={inputCls} value={album.display_artist} onChange={(e) => setA("display_artist", e.target.value)} placeholder="Auto from artists above" />
                </div>
                <div>
                  <label className={labelCls}>Primary Metadata Language</label>
                  <LanguageSelect value={album.primary_language} onChange={(v) => setA("primary_language", v)} testId="album-primary-lang" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelCls}>Genre</label>
                    <input className={inputCls} value={album.genre} onChange={(e) => setA("genre", e.target.value)} placeholder="e.g. World" />
                  </div>
                  <div>
                    <label className={labelCls}>Sub-genre</label>
                    <input className={inputCls} value={album.subgenre} onChange={(e) => setA("subgenre", e.target.value)} placeholder="e.g. India" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Label {user?.role !== "label" && <span className="text-slate-500 normal-case tracking-normal">(default: Noizbloc Private Limited)</span>}</label>
                  <input
                    data-testid="album-label"
                    className={inputCls}
                    value={album.label}
                    onChange={(e) => setA("label", e.target.value)}
                    placeholder={user?.role === "label" ? "Your label name" : "Noizbloc Private Limited"}
                  />
                </div>
                <div>
                  <label className={labelCls}>Parent Label <span className="text-slate-500 normal-case tracking-normal">(fixed)</span></label>
                  <input
                    data-testid="album-parent-label"
                    className={`${inputCls} opacity-70 cursor-not-allowed`}
                    value={album.parent_label || "Noizbloc Private Limited"}
                    readOnly
                    disabled
                  />
                  <div className="mt-1 text-[10px] text-slate-500">All Noizbloc releases roll up to <span className="text-white">Noizbloc Private Limited</span>.</div>
                </div>
                <div>
                  <label className={labelCls}>Catalog #</label>
                  <input className={inputCls} value={album.catalog_number} onChange={(e) => setA("catalog_number", e.target.value)} placeholder="e.g. NB0001" />
                </div>
                <div>
                  <label className={labelCls}>UPC / EAN</label>
                  <input data-testid="album-upc" className={inputCls} value={album.upc} onChange={(e) => setA("upc", e.target.value)} placeholder="Leave blank if this is a new release" />
                  <div className="mt-1 text-[10px] text-slate-500">Only enter a UPC if the release was previously distributed elsewhere. Noizbloc issues a new UPC at delivery.</div>
                </div>
                <div>
                  <label className={labelCls}>Release Date *</label>
                  <DatePickerField value={album.release_date} onChange={(v) => setA("release_date", v)} testId="album-release-date" placeholder="Pick release date" />
                </div>
                <div>
                  <label className={labelCls}>Original Release Date</label>
                  <DatePickerField value={album.original_release_date} onChange={(v) => setA("original_release_date", v)} testId="album-original-date" placeholder="Only for re-releases" />
                </div>
                <div>
                  <label className={labelCls}>Pre-Order Date</label>
                  <DatePickerField value={album.pre_order_date} onChange={(v) => setA("pre_order_date", v)} testId="album-preorder-date" placeholder="Optional" />
                </div>
                <div>
                  <label className={labelCls}>Audio Resolution</label>
                  <Select value={album.audio_resolution} onValueChange={(v) => setA("audio_resolution", v)}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-[42px] rounded-xl text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                      <SelectItem value="High">High (24-bit / lossless)</SelectItem>
                      <SelectItem value="Standard">Standard (16-bit)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-6 pt-1 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <Switch checked={!!album.compilation} onCheckedChange={(v) => setA("compilation", v)} /> This is a compilation
                </label>
              </div>

              {/* Dolby Atmos paid add-on */}
              <div
                data-testid="dolby-atmos-card"
                className={`rounded-2xl border p-4 flex items-start gap-4 transition-colors ${
                  album.dolby_atmos
                    ? "border-blue-500/40 bg-gradient-to-br from-blue-600/[0.08] via-blue-500/[0.04] to-transparent"
                    : "border-white/10 bg-white/[0.02]"
                }`}
              >
                <div className="h-11 w-11 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center shrink-0">
                  <Sparkles size={18} className="text-blue-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-base text-white">Deliver in Dolby Atmos</div>
                    <span className="text-[10px] tracking-widest uppercase font-bold text-blue-300 bg-blue-500/10 border border-blue-500/25 rounded-full px-2 py-0.5">Paid add-on · £40</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-400 max-w-md">
                    Add spatial audio delivery to Apple Music, Tidal HiFi Plus and Amazon Music Unlimited.
                    Requires an Atmos master (BWAV or ADM) — attach it in the Tracks tab.
                  </p>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-300 shrink-0">
                  <Switch
                    checked={!!album.dolby_atmos}
                    onCheckedChange={(v) => setA("dolby_atmos", v)}
                    data-testid="dolby-atmos-toggle"
                  />
                </label>
              </div>

              <div>
                <label className={labelCls}>{isVideo ? "Thumbnail" : "Cover Art"}</label>
                <label className="cursor-pointer flex items-center gap-3 rounded-xl border border-dashed border-white/15 hover:border-white/30 bg-white/[0.02] p-3 transition-colors">
                  {album.cover_url ? (
                    <img
                      src={mediaUrl(album.cover_url)}
                      alt={isVideo ? "thumbnail" : "cover"}
                      className={`rounded-lg object-cover ${isVideo ? "h-10 w-[71px]" : "h-14 w-14"}`}
                    />
                  ) : (
                    <div className={`rounded-lg bg-white/5 flex items-center justify-center text-slate-500 ${isVideo ? "h-10 w-[71px]" : "h-14 w-14"}`}>
                      {uploading.cover ? <Loader2 size={18} className="animate-spin" /> : <ImageIcon size={18} />}
                    </div>
                  )}
                  <div className="text-sm text-slate-300">
                    <div>{album.cover_url ? (isVideo ? "Change thumbnail" : "Change cover art") : (isVideo ? "Upload thumbnail" : "Upload cover art")}</div>
                    <div className="text-xs text-slate-500">
                      {isVideo
                        ? "JPG / PNG · Landscape 16:9, min 1920×1080"
                        : "JPG / PNG / WEBP · Square, min 3000×3000"}
                    </div>
                  </div>
                  <input
                    data-testid="album-cover-input"
                    type="file"
                    accept={isVideo ? "image/jpeg,image/png,.jpg,.jpeg,.png" : "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"}
                    className="hidden"
                    onChange={uploadCover}
                  />
                </label>
              </div>

              <div>
                <label className={labelCls}>Liner Notes</label>
                <textarea className={inputCls + " min-h-[80px]"} value={album.liner_notes} onChange={(e) => setA("liner_notes", e.target.value)} placeholder="Credits, thanks, dedication…" />
              </div>
            </TabsContent>

            <TabsContent value="tracks" className="space-y-4">
              {isVideo && tracks.length > 1 && (
                <div className="rounded-xl border border-fuchsia-500/25 bg-fuchsia-500/[0.05] px-4 py-3 text-xs text-fuchsia-200">
                  Music Video releases distribute a single video. Extra tracks will be ignored on delivery.
                </div>
              )}
              {tracks.map((t, i) => (
                <TrackRow
                  key={i}
                  idx={i}
                  track={t}
                  isVideo={isVideo}
                  onChange={(next) => {
                    const copy = [...tracks]; copy[i] = next; setTracks(copy);
                  }}
                  onDelete={() => setTracks(tracks.filter((_, j) => j !== i))}
                />
              ))}
              {!isVideo && (
                <button
                  type="button"
                  data-testid="add-track-btn"
                  onClick={() => setTracks([...tracks, { ...emptyTrack(), track_number: tracks.length + 1 }])}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-dashed border-white/15 hover:border-white/30 py-3 text-sm text-slate-300"
                >
                  <Plus size={16} /> Add track
                </button>
              )}
            </TabsContent>

            <TabsContent value="rights" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>C Line (composition ©)</label>
                  <input data-testid="album-c-line" className={inputCls} value={album.c_line} onChange={(e) => setA("c_line", e.target.value)} placeholder="© 2026 Owner" />
                </div>
                <div>
                  <label className={labelCls}>P Line (recording ℗)</label>
                  <input data-testid="album-p-line" className={inputCls} value={album.p_line} onChange={(e) => setA("p_line", e.target.value)} placeholder="℗ 2026 Owner" />
                </div>
              </div>
              <ContribList
                label="Performing Contributors (e.g. Vocals, Guitar)"
                items={album.performing_contributors}
                setItems={(v) => setA("performing_contributors", v)}
                testIdPrefix="perf-contrib"
              />
              <ContribList
                label="Non-Performing Contributors (e.g. Musical Director, Mixer)"
                items={album.non_performing_contributors}
                setItems={(v) => setA("non_performing_contributors", v)}
                testIdPrefix="nonperf-contrib"
              />
            </TabsContent>

            <TabsContent value="distribution" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Spotify Link (post-delivery)</label>
                  <input className={inputCls} value={album.spotify_link} onChange={(e) => setA("spotify_link", e.target.value)} placeholder="spotify:album:… or URL" />
                </div>
                <div>
                  <label className={labelCls}>Apple Music Link</label>
                  <input className={inputCls} value={album.apple_music_link} onChange={(e) => setA("apple_music_link", e.target.value)} placeholder="Apple Music URL" />
                </div>
              </div>

              <TerritoryRestrictions album={album} setA={setA} />

              <ServiceRestrictions
                album={album}
                setA={setA}
                services={album.type === "Music Video" ? VIDEO_SERVICES : SERVICES}
                testIdPrefix={album.type === "Music Video" ? "video-services" : "services"}
              />
            </TabsContent>

            <div className="sticky bottom-0 -mx-6 px-6 pt-4 mt-6 border-t border-white/10 bg-[#0b1220] flex items-center justify-between gap-3">
              <div className="text-xs text-slate-500">
                Saved as <span className="text-slate-300">Draft</span>. Submit for review from the release list.
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  className="rounded-full border border-white/15 text-white text-sm px-5 py-2.5 hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="release-editor-save"
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium text-sm px-6 py-2.5 hover:bg-slate-200 disabled:opacity-60 transition-colors"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : "Create draft"}
                </button>
              </div>
            </div>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
